import { Image as ImageIcon, Trash2, Upload } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Spinner } from '@components/spinner';
import { cn } from '@core/utils/cn';

export interface ImageUploadProps {
    onUpload: (file: File) => Promise<void>;
    onRemove?: () => void;
    loading?: boolean;
    error?: string;
    previewUrl?: string;
    accept?: string;
    maxSizeMB?: number;
    label?: string;
    helperText?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
    onUpload,
    onRemove,
    loading = false,
    error,
    previewUrl,
    accept = 'image/*',
    maxSizeMB = 5,
    label = 'Upload Image',
    helperText = 'Drag and drop an image here, or click to select'
}) => {
    const [dragActive, setdragActive] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const validateFile = useCallback(
        (file: File): string | null => {
            if (!file.type.startsWith('image/')) {
                return 'Please select an image file';
            }
            const maxSizeBytes = maxSizeMB * 1024 * 1024;
            if (file.size > maxSizeBytes) {
                return `File size must be less than ${maxSizeMB}MB`;
            }
            return null;
        },
        [maxSizeMB]
    );

    const handleFile = useCallback(
        async (file: File) => {
            const message = validateFile(file);
            if (message) {
                setValidationError(message);
                return;
            }
            setValidationError(null);
            await onUpload(file);
        },
        [onUpload, validateFile]
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setdragActive(false);
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                handleFile(files[0]);
            }
        },
        [handleFile]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setdragActive(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setdragActive(false);
    }, []);

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
                handleFile(files[0]);
            }
        },
        [handleFile]
    );

    return (
        <div>
            <p className="mb-2 text-sm font-medium">{label}</p>
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleClick}
                className={cn(
                    'cursor-pointer rounded-lg border border-dashed p-6 text-center transition-colors hover:border-primary hover:bg-accent/50',
                    dragActive && 'border-primary bg-accent/50'
                )}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInputChange}
                    className="hidden"
                />
                {loading ? (
                    <div className="flex flex-col items-center gap-4">
                        <Spinner size="lg" />
                        <p className="text-sm text-muted-foreground">
                            Uploading...
                        </p>
                    </div>
                ) : previewUrl ? (
                    <div className="flex flex-col items-center gap-4">
                        <img
                            src={previewUrl}
                            alt="Preview"
                            className="max-h-[200px] max-w-full rounded-lg"
                        />
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" size="sm">
                                <Upload />
                                Change Image
                            </Button>
                            {onRemove && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemove();
                                    }}
                                >
                                    <Trash2 />
                                    Remove
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-4">
                        <ImageIcon className="size-12 text-muted-foreground" />
                        <p className="text-sm">{helperText}</p>
                        <Button type="button" variant="outline" size="sm">
                            <Upload />
                            Select Image
                        </Button>
                    </div>
                )}
            </div>
            {(validationError || error) && (
                <Alert variant="destructive" className="mt-4">
                    <AlertDescription>
                        {validationError || error}
                    </AlertDescription>
                </Alert>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
                Supported formats: JPG, PNG, GIF. Max size: {maxSizeMB}MB
            </p>
        </div>
    );
};
